import React from 'react';
import {useTranslation} from 'react-i18next';

import {Icon} from '@edna/components/primitives';
import {useEvent} from '@edna/hooks';
import ArrowUpward from '@edna/icons/arrowUpward.svg';
import KeyboardArrowDownIcon from '@edna/icons/keyboardArrowDown.svg';

import {useAuthorizedMetrics} from 'src/hooks';
import {scrollDown, scrollToTop} from 'src/utils';

import * as S from './style';

const ScrollUpButton = React.memo<{
  reserve?: number;
}>(({reserve = 200}) => {
  const {t} = useTranslation();
  const {sendMetrics} = useAuthorizedMetrics();
  const [isVisible, setVisibility] = React.useState(false);

  const handleScroll = React.useCallback(() => {
    const visible = document.documentElement.scrollTop > reserve;

    setVisibility(visible);
  }, [reserve]);

  useEvent('scroll', handleScroll);

  if (!isVisible) {
    return null;
  }

  return (
    <S.ScrollUpButton
      title={t('ScrollUpButton:title')}
      onClick={() => {
        sendMetrics('list-up-button-click');
        scrollToTop();
      }}
    >
      <Icon as={ArrowUpward} size="20px" />
    </S.ScrollUpButton>
  );
});

ScrollUpButton.displayName = 'ScrollUpButton';

const ScrollDownButton = React.memo<{
  reserve?: number;
  elementRef: React.RefObject<HTMLDivElement | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: any;
}>(({reserve = 200, elementRef, watch}) => {
  const {t} = useTranslation();
  const [isVisible, setVisibility] = React.useState(false);

  React.useEffect(() => {
    setVisibility(false);
  }, [watch]);

  React.useEffect(() => {
    const el = elementRef.current;

    if (!el) {
      return;
    }

    const handleScroll = () => {
      const {scrollHeight, scrollTop, clientHeight} = el;

      setVisibility(scrollHeight - scrollTop - clientHeight > reserve);
    };

    el.addEventListener('scroll', handleScroll);

    return () => el.removeEventListener('scroll', handleScroll);
  }, [elementRef.current, reserve]);

  if (!isVisible) {
    return null;
  }

  return (
    <S.ScrollDownButton
      title={t('ScrollUpButton:title')}
      onClick={() => {
        if (elementRef.current) {
          scrollDown(elementRef.current);
        }
      }}
    >
      <Icon as={KeyboardArrowDownIcon} size="20px" />
    </S.ScrollDownButton>
  );
});

ScrollDownButton.displayName = 'ScrollDownButton';

export {ScrollDownButton};
export default ScrollUpButton;
